import React, { useState, useEffect } from "react";
import {
    Box, TextField, Button, Typography, Divider, Card,
    Grid
} from "@mui/material";
import { db } from "../../firebase";
import { collection, getDocs, query, where, Timestamp, orderBy, onSnapshot } from "firebase/firestore";

function Purchasereport() {
    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [data, setData] = useState({ purchases: [], payments: [], expenses: [] });
    const [loading, setLoading] = useState(false);
    const [dealers, setDealers] = useState([]);
    const [payModes, setPayModes] = useState([]);
    const [products, setProducts] = useState([]);
    const [expenseHeads, setExpenseHeads] = useState([]);

    // Live sync metadata tables
    useEffect(() => {
        const unsubdealers = onSnapshot(collection(db, "dealers"), snap => {
            setDealers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const unsubPayModes = onSnapshot(collection(db, "payment_modes"), snap => {
            setPayModes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const unsubProducts = onSnapshot(collection(db, "purchase_products"), snap => {
            setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const unsubExpenseHeads = onSnapshot(collection(db, "expenseHeads"), snap => {
            setExpenseHeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => {
            unsubdealers();
            unsubPayModes();
            unsubProducts();
            unsubExpenseHeads();
        };
    }, []);

    // Helpers to resolve metadata names dynamically
    const getCustName = (id) => dealers.find(c => c.id === id)?.dealerName || id || "Unknown";
    const getPayModeName = (id) => payModes.find(m => m.id === id)?.payMode || "Cash";
    const getProductName = (id) => products.find(p => p.id === id)?.productName || "Unknown Product";
    const getExpenseHeadName = (id) => expenseHeads.find(e => e.id === id)?.name;

    const fetchAllData = async () => {
        if (!fromDate || !toDate) return alert("Select date range");
        setLoading(true);

        try {
            const start = Timestamp.fromDate(new Date(fromDate + " 00:00:00"));
            const end = Timestamp.fromDate(new Date(toDate + " 23:59:59"));

            // Queries matching specified range structures
            const qpurchases = query(
                collection(db, "purchases"), 
                where("purchasedate", ">=", fromDate), 
                where("purchasedate", "<=", toDate), 
                orderBy("purchasedate", "desc")
            );
            const qPay = query(
                collection(db, "dealerPayments"), 
                where("date", ">=", start), 
                where("date", "<=", end), 
                orderBy("date", "desc")
            );
            const qExpenses = query(
                collection(db, "otherExpenses"),
                where("date", ">=", start),
                where("date", "<=", end),
                orderBy("date", "desc")
            );

            // Fetch simultaneously
            const [purchasesnap, paySnap, expenseSnap] = await Promise.all([
                getDocs(qpurchases), 
                getDocs(qPay),
                getDocs(qExpenses)
            ]);

            // Helper local mapping for performance inside iteration loop
            const dealerMap = {};
            dealers.forEach(d => { dealerMap[d.id] = d.dealerName; });

            const purchases = purchasesnap.docs.map(doc => {
                const pData = doc.data();
                return {
                    id: doc.id,
                    ...pData,
                    displayDealer: dealerMap[pData.dealerId] || "Unknown Dealer",
                    calculatedTotal: Number(pData.grandtotal || 0)
                };
            });

            const payments = paySnap.docs.map(doc => {
                const payData = doc.data();
                return {
                    id: doc.id,
                    ...payData,
                    displayDealer: getCustName(payData.dealerId || payData.Dealer),
                    displayMode: getPayModeName(payData.paymentMode || payData.payMode)
                };
            });

            const expenses = expenseSnap.docs.map(doc => {
                const expData = doc.data();
                return {
                    id: doc.id,
                    ...expData,
                    displayHead: getExpenseHeadName(expData.expenseHeadId),
                    displayDate: expData.date?.toDate().toISOString().split("T")[0] || ""
                };
            });

            setData({ purchases, payments, expenses });
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    // Calculate Column Totals
    const totalPurchaseAmt = (data.purchases || []).reduce((s, i) => s + (i.calculatedTotal || 0), 0);
    const totalCollectionAmt = (data.payments || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const totalExpenseAmt = (data.expenses || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const combinedGrandTotalOutflow = totalPurchaseAmt + totalExpenseAmt;

    useEffect(() => {
        if (dealers.length > 0 || payModes.length > 0 || products.length > 0) {
            fetchAllData();
        }
    }, [dealers, payModes, products]);

    // Core layout stylings
    const cellStyle = { border: '1px solid #000', padding: '5px 6px', fontSize: '0.82rem', color: '#000' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: '#eeeeee', textAlign: 'center', fontSize: '0.85rem' };
    const itemRowStyle = { fontSize: '0.78rem', borderBottom: '1px dashed #ccc', padding: '2px 0' };

    return (
        <Box p={1} sx={{ bgcolor: "#fff" }}>
            {/* Control Filters */}
            <Card sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', borderRadius: '8px' }} className="no-print">
                <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
                <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} />
                <Button variant="contained" onClick={fetchAllData} disabled={loading} sx={{ minWidth: '120px' }}>
                    {loading ? "Fetching..." : "Generate"}
                </Button>
                <Button variant="outlined" onClick={() => window.print()}>Print PDF</Button>
            </Card>

            {/* Print Output Wrapper Grid */}
            <Grid className="printable-area p-2">
                <Box sx={{ border: '2px solid #000', p: 2 }}>
                    <Box textAlign="center" mb={2}>
                        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold', letterSpacing: 1 }}>
                            MASTER FINANCIAL SUMMARY REPORT ({fromDate} TO {toDate})
                        </Typography>
                        <Divider sx={{ my: 2, bgcolor: '#000', height: '2px' }} />
                    </Box>

                    {/* CONSOLIDATED METRICS OVERVIEW */}
                    <Box mb={4}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={headerStyle}>1. Total Purchases</th>
                                    <th style={headerStyle}>2. Total Operations Expenses</th>
                                    <th style={headerStyle}>Combined Gross Liabilities (1 + 2)</th>
                                    <th style={headerStyle}>Actual Vendor Paid Outflows</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold' }}>₹{totalPurchaseAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold', color: '#b71c1c' }}>₹{totalExpenseAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold', backgroundColor: '#fff8e1' }}>₹{combinedGrandTotalOutflow.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold', color: '#1b5e20' }}>₹{totalCollectionAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                            </tbody>
                        </table>
                    </Box>

                    {/* SECTION 1: Purchases Details */}
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>I. PURCHASE LOG</Typography>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px' }}>
                        <thead>
                            <tr>
                                <th style={headerStyle}>Sr.</th>
                                <th style={headerStyle}>Date</th>
                                <th style={headerStyle}>Purchase No</th>
                                <th style={headerStyle}>Dealer</th>
                                <th style={headerStyle}>Items purchased (Product / Qty / Rate)</th>
                                <th style={headerStyle}>Purchase Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.purchases.length === 0 ? (
                                <tr><td colSpan={6} style={{ ...cellStyle, textAlign: 'center', color: '#666' }}>No records found</td></tr>
                            ) : (
                                data.purchases.map((row, index) => (
                                    <tr key={row.id} style={{ verticalAlign: 'top' }}>
                                        <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                                        <td style={{ ...cellStyle, width: '100px', textAlign: 'center' }}>{row.purchasedate}</td>
                                        <td style={{ ...cellStyle, width: '100px', textAlign: 'center' }}><strong>{row.purchaseno}</strong></td>
                                        <td style={{ ...cellStyle, width: '180px' }}><strong>{row.displayDealer}</strong></td>
                                        <td style={cellStyle}>
                                            {row.items && Array.isArray(row.items) && row.items.length > 0 ? (
                                                <Box>
                                                    {row.items.map((item, idx) => (
                                                        <Box key={idx} sx={itemRowStyle} display="flex" justifyContent="space-between">
                                                            <span style={{ fontWeight: '500' }}>• {getProductName(item.productId)}</span>
                                                            <span>{item.qty || 0} qty @ ₹{Number(item.rate || 0).toFixed(2)}</span>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <span style={{ color: '#777', fontStyle: 'italic' }}>No items recorded</span>
                                            )}
                                        </td>
                                        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', width: '130px' }}>
                                            ₹{(row.calculatedTotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))
                            )}
                            <tr style={{ backgroundColor: '#eeeeee' }}>
                                <td colSpan={5} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '0.90rem' }}>TOTAL PURCHASE AMOUNT</td>
                                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '0.90rem' }}>
                                    ₹{totalPurchaseAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* SECTION 2: Expense Management logs */}
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>II. OTHER OPERATION EXPENSES</Typography>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px' }}>
                        <thead>
                            <tr>
                                <th style={headerStyle}>Sr.</th>
                                <th style={headerStyle}>Expense Date</th>
                                <th style={headerStyle}>Expense Category Head</th>
                                <th style={headerStyle}>Description Note</th>
                                <th style={headerStyle}>Expense Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.expenses.length === 0 ? (
                                <tr><td colSpan={5} style={{ ...cellStyle, textAlign: 'center', color: '#666' }}>No expense items recorded in this window</td></tr>
                            ) : (
                                data.expenses.map((row, index) => (
                                    <tr key={row.id}>
                                        <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                                        <td style={{ ...cellStyle, textAlign: 'center', width: '120px' }}>{row.displayDate}</td>
                                        <td style={{ ...cellStyle, fontWeight: '600' }}>{row.displayHead}</td>
                                        <td style={cellStyle}>{row.description || "—"}</td>
                                        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', width: '130px' }}>
                                            ₹{(Number(row.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))
                            )}
                            <tr style={{ backgroundColor: '#eeeeee' }}>
                                <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '0.90rem' }}>TOTAL OPERATIONAL EXPENSES</td>
                                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '0.90rem' }}>
                                    ₹{totalExpenseAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* SECTION 3: Vendor Payments History Logs */}
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>III. DEALER CASH & RECORDED OUT-PAYMENTS</Typography>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5px' }}>
                        <thead>
                            <tr>
                                <th style={headerStyle}>Sr.</th>
                                <th style={headerStyle}>Payment Date</th>
                                <th style={headerStyle}>Dealer Name</th>
                                <th style={headerStyle}>Payment Mode</th>
                                <th style={headerStyle}>Paid Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.payments.length === 0 ? (
                                <tr><td colSpan={5} style={{ ...cellStyle, textAlign: 'center', color: '#666' }}>No payout metrics recorded</td></tr>
                            ) : (
                                data.payments.map((row, index) => (
                                    <tr key={row.id}>
                                        <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                                        <td style={{ ...cellStyle, textAlign: 'center', width: '120px' }}>{row.date?.toDate().toLocaleDateString()}</td>
                                        <td style={cellStyle}>{row.displayDealer}</td>
                                        <td style={{ ...cellStyle, textAlign: 'center', width: '140px' }}>{row.displayMode}</td>
                                        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', width: '130px' }}>
                                            ₹{(Number(row.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))
                            )}
                            <tr style={{ backgroundColor: '#eeeeee' }}>
                                <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '0.95rem' }}>TOTAL VENDOR SETTLEMENTS PAYOUT</td>
                                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                    ₹{totalCollectionAmt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Box>
            </Grid>

            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .printable-area, .printable-area * { visibility: visible; }
                    .printable-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        border: none !important; 
                    }
                    .no-print { display: none !important; }
                    @page { size: A4; margin: 10mm; }
                    table { page-break-inside: auto; }
                    tr { page-break-inside: avoid; page-break-after: auto; }
                }
            `}</style>
        </Box>
    );
}

export default Purchasereport;